import React, { FunctionComponent, useMemo } from 'react'
import { Link, useRouteMatch } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import classNames from 'classnames'

import { RootState } from '../../../redux/store'
import { ROUTER } from '../../../config'
import Lock from '../../lock'
import { isAuthenticated } from '../../../helpers'
import useHost from '../../../hooks/useHost'
import { HOSTS } from '../../../constants'
import { StyledNav } from './style'

const LoginButton = () => {
  return (
    <li>
      <a href={ROUTER.login} data-testid='header-login-button'>
        <FormattedMessage id='navigation.login' />
      </a>
    </li>
  )
}

interface INavLink {
  label: JSX.Element | string
  to: string
  activeOnlyWhenExact?: boolean
  locked?: boolean
}

const NavLink: FunctionComponent<INavLink> = ({
  label,
  to,
  activeOnlyWhenExact,
  locked,
}) => {
  let match = useRouteMatch({
    path: to,
    exact: activeOnlyWhenExact,
  })

  // Generate testId based on the route
  const getTestId = (path: string) => {
    if (path.includes('/calendar/tournaments')) return 'nav-calendar-link'
    if (path.includes('/watch')) return 'nav-watch-link'
    if (path.includes('/followed')) return 'nav-followed-link'
    if (path.includes('/rating')) return 'nav-rating-link'

    return `nav-link-${path.replace(/\//g, '-')}`
  }

  return (
    <li className={`${match ? 'active' : ''} ${locked ? 'locked' : ''}`}>
      <Link to={to} data-testid={getTestId(to)}>
        {label}
        {locked && <Lock />}
      </Link>
    </li>
  )
}

interface IHeaderNav {
  navStatus?: boolean
}

const HeaderNav: FunctionComponent<IHeaderNav> = ({ navStatus }) => {
  const isUserOnline = useSelector<RootState>(state => state.userOnline)
  const isM87Host = useHost(HOSTS.m87PassportGg)
  const isM87DevHost = useHost(HOSTS.m87DevPassportGg)
  const isM87 = isM87Host || isM87DevHost
  const isFollowedVisible = useMemo(
    () => !!isAuthenticated() && !isM87,
    [isM87]
  )

  const calendarLabel = useMemo(
    () =>
      isM87 ? (
        <FormattedMessage id='header.tournaments' />
      ) : (
        <FormattedMessage id='header.calendar' />
      ),
    [isM87]
  )

  return (
    <>
      <StyledNav
        className={classNames('headerNavLinksColor')}
        navStatus={navStatus}
        data-testid='main-navigation'
      >
        <NavLink to='/calendar/tournaments' label={calendarLabel} />
        <NavLink
          to='/watch'
          label={<FormattedMessage id='id.performance.dota.matches' />}
        />

        {isFollowedVisible && (
          <NavLink
            to='/followed/tournaments'
            label={<FormattedMessage id='header.followed' />}
          />
        )}
        <NavLink
          to='/rating'
          label={<FormattedMessage id='ratings.rating' />}
        />
        <li>
          <a
            href='https://passport.gg/gamersupport'
            target={'_blank'}
            style={{ textDecoration: 'none' }}
            data-testid='nav-support-link'
            rel='noreferrer'
          >
            <FormattedMessage id='footer.support' />
          </a>
        </li>
        {!isUserOnline && <LoginButton />}
      </StyledNav>
    </>
  )
}

export default HeaderNav
